import React, { useEffect, useMemo, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import axios from 'axios';
import { Download } from 'lucide-react';
import { useCarichiTable } from '../../pages/CarichiPage/hooks/useCarichiTable';
import { CarichiToolbar } from '../../pages/CarichiPage/components/CarichiToolbar';
import { CarichiTable } from '../../pages/CarichiPage/components/CarichiTable';
import { ColumnsDrawer } from '../../pages/CarichiPage/components/ColumnsDrawer';
import { ManualSessionDialog } from '../../pages/CarichiPage/components/ManualSessionDialog';
import { ImportCSVWizard } from '../../pages/CarichiPage/components/csv-wizard/ImportCSVWizard';
import { GpsUploadDialog } from '../../pages/CarichiPage/components/GpsUploadDialog';
import { FiltersDrawer } from '../../pages/CarichiPage/components/FiltersDrawer';
import { ActiveFiltersChips } from '../../pages/CarichiPage/components/ActiveFiltersChips';
import { CalendarSubview } from '../../pages/CarichiPage/components/subviews/CalendarSubview';
import { ReportAthleteSubview } from '../../pages/CarichiPage/components/subviews/ReportAthleteSubview';
import { DayDetailModal } from '../../pages/CarichiPage/components/subviews/DayDetailModal';
import { ManualEntryWizard } from '../../pages/CarichiPage/components/m6-2/ManualEntryWizard';
import { baseUrl } from '../../hooks/useEndpoints';
import { groupsEndpoints } from '../../endpoints/sessions';
import { ICustomGroup } from '../../models/sessions';
import { leoTokens, installLeoStyles } from '../../pages/CarichiPage/theme/leoTokens';

interface ITeamLite {
  _id: string;
  name: string;
  users: string[];
  level?: string;
}

interface CarichiTeamTabProps {
  teamId: string;
  teamName?: string;
  teamUsers?: string[];
}

/**
 * Tab Carichi per DetailTeamPage.
 * Riusa tutti i componenti di CarichiPage con teamId fisso dal context team.
 * Non mostra il dropdown di selezione team (teamId è già noto).
 */
export const CarichiTeamTab: React.FC<CarichiTeamTabProps> = ({
  teamId,
  teamName = '',
  teamUsers = [],
}) => {
  useEffect(() => { installLeoStyles(); }, []);

  const [columnsOpen, setColumnsOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [csvWizardOpen, setCsvWizardOpen] = useState(false);
  const [gpsUploadOpen, setGpsUploadOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [manualPrefill, setManualPrefill] = useState<{ date?: string; athleteId?: string; athleteName?: string }>({});
  const [dayDetail, setDayDetail] = useState<{ open: boolean; date: string; athleteId?: string; athleteName?: string }>({ open: false, date: '' });
  const [rosterAthletes, setRosterAthletes] = useState<{ _id: string; firstName: string; lastName: string; roles?: string[] }[]>([]);
  const [customGroups, setCustomGroups] = useState<ICustomGroup[]>([]);
  const [overrideColumns, setOverrideColumns] = useState<string[] | undefined>();
  const [manualWizardOpen, setManualWizardOpen] = useState(false);

  const ctx = useCarichiTable(teamId);

  // Carica roster atleti dal teamUsers passato via prop
  useEffect(() => {
    if (!teamUsers || teamUsers.length === 0) { setRosterAthletes([]); return; }
    const ids = teamUsers.join(',');
    axios.get<{ docs?: any[] } | any[]>(`${baseUrl}/users?_id=${ids}&limit=200`).then((res) => {
      const data = res.data;
      const list = Array.isArray(data) ? data : data.docs ?? [];
      setRosterAthletes(list.map((u: any) => ({
        _id: u._id, firstName: u.firstName, lastName: u.lastName,
        roles: u.roles ?? u.position ? [u.roles ?? u.position].flat().filter(Boolean) : undefined,
      })));
    }).catch(() => {
      setRosterAthletes((teamUsers).map((id) => ({ _id: id, firstName: id.slice(-4), lastName: '' })));
    });
  }, [teamUsers]);

  // Carica custom groups
  useEffect(() => {
    if (!teamId) return;
    groupsEndpoints.list(teamId).then((res) => setCustomGroups(res.data)).catch(() => setCustomGroups([]));
  }, [teamId]);

  const activeFilters = ctx.filters;
  const realActiveFiltersCount = useMemo(() => (
    (activeFilters.roles?.length ?? 0) +
    (activeFilters.sessionTypes?.length ?? 0) +
    (activeFilters.intensityBuckets?.length ?? 0) +
    (activeFilters.activityCategoryFilter?.length ?? 0) +
    (activeFilters.baselineFilter?.length ?? 0) +
    (activeFilters.customGroupId ? 1 : 0) +
    (activeFilters.percentileFilter ? 1 : 0)
  ), [activeFilters]);

  const handleExportPdf = async () => {
    try {
      const response = await axios.post(`${baseUrl}/exports/pdf`, {
        teamId, teamName,
        tableQuery: { mode: ctx.mode, unitMode: ctx.unitMode, presetId: ctx.presetId, periodStart: ctx.period.start, periodEnd: ctx.period.end },
      }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Carichi_${teamName}_${ctx.period.start}_${ctx.period.end}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) { alert(`Errore export PDF: ${e?.message ?? 'sconosciuto'}`); }
  };

  // Costrutto team lite per compatibilità con sottocomponenti che richiedono ITeamLite
  const teamLite: ITeamLite = { _id: teamId, name: teamName, users: teamUsers };

  return (
    <Box className="leo-scope" bg={leoTokens.bg0} width="100%">
      <Box bg="white" width="100%">
        <CarichiToolbar
          catalog={ctx.catalog}
          subview={ctx.subview} setSubview={ctx.setSubview}
          mode={ctx.mode} setMode={ctx.setMode}
          unitMode={ctx.unitMode} setUnitMode={ctx.setUnitMode}
          presetId={ctx.presetId}
          setPresetId={(p) => { setOverrideColumns(undefined); ctx.setPresetId(p); }}
          period={ctx.period} setPeriod={ctx.setPeriod}
          kpiMetric={ctx.kpiMetric} setKpiMetric={ctx.setKpiMetric}
          onOpenColumns={() => setColumnsOpen(true)}
          onOpenManualSession={() => setManualOpen(true)}
          onOpenManualWizard={() => setManualWizardOpen(true)}
          onOpenCsvWizard={() => setCsvWizardOpen(true)}
          onOpenGpsUpload={() => setGpsUploadOpen(true)}
          onOpenFilters={() => setFiltersOpen(true)}
          onExportPdf={handleExportPdf}
          activeFiltersCount={realActiveFiltersCount}
        />

        <ActiveFiltersChips filters={ctx.filters} onChange={ctx.setFilters} customGroups={customGroups} />

        {ctx.subview === 'table' && ctx.table && ctx.table.meta.sampleSize === 0 && (
          <Box bg="#fef3c7" p={3} fontSize="sm" color="#854d0e">
            Nessuna sessione nel periodo per questo team. Clicca{' '}
            <strong>
              <Download size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />
              Importa
            </strong>{' '}
            per inserire un dato manuale.
          </Box>
        )}

        {ctx.subview === 'table' && (
          <CarichiTable table={ctx.table} catalog={ctx.catalog} isLoading={ctx.isLoading} isError={ctx.isError} />
        )}

        {ctx.subview === 'calendar' && (
          <CalendarSubview
            teamId={teamId}
            rosterAthletes={rosterAthletes}
            period={ctx.period}
            onCellClick={(date, athleteId) => {
              const ath = rosterAthletes.find((a) => a._id === athleteId);
              setDayDetail({ open: true, date, athleteId, athleteName: ath ? `${ath.firstName} ${ath.lastName}` : undefined });
            }}
          />
        )}

        {ctx.subview === 'report' && (
          <ReportAthleteSubview rosterAthletes={rosterAthletes} period={ctx.period} />
        )}
      </Box>

      <ColumnsDrawer open={columnsOpen} onClose={() => setColumnsOpen(false)} catalog={ctx.catalog}
        activeColumns={overrideColumns ?? ctx.activeColumns} onApply={(cols) => setOverrideColumns(cols)} />

      <ManualSessionDialog open={manualOpen}
        onClose={() => { setManualOpen(false); setManualPrefill({}); }}
        teamId={teamId} rosterAthleteIds={teamUsers}
        defaultDate={manualPrefill.date ?? ctx.period.end}
        prefillAthleteId={manualPrefill.athleteId} prefillAthleteName={manualPrefill.athleteName} />

      <ImportCSVWizard open={csvWizardOpen} onClose={() => setCsvWizardOpen(false)} catalog={ctx.catalog}
        teamId={teamId} rosterAthletes={rosterAthletes} onCompleted={() => ctx.refetch()} />

      <GpsUploadDialog open={gpsUploadOpen} onClose={() => setGpsUploadOpen(false)}
        teamId={teamId} rosterAthletes={rosterAthletes} onCompleted={() => ctx.refetch()} />

      <FiltersDrawer open={filtersOpen} onClose={() => setFiltersOpen(false)}
        filters={ctx.filters} onApply={(f) => ctx.setFilters(f)}
        rosterAthletes={rosterAthletes} customGroups={customGroups.map((g) => ({ _id: g._id, name: g.name }))} />

      <ManualEntryWizard
        open={manualWizardOpen}
        onClose={() => setManualWizardOpen(false)}
        teamId={teamId}
        teamName={teamName}
        onCompleted={() => ctx.refetch()}
      />

      <DayDetailModal open={dayDetail.open} onClose={() => setDayDetail({ ...dayDetail, open: false })}
        date={dayDetail.date} athleteId={dayDetail.athleteId} athleteName={dayDetail.athleteName}
        onImportClick={() => {
          setManualPrefill({ date: dayDetail.date, athleteId: dayDetail.athleteId, athleteName: dayDetail.athleteName });
          setManualOpen(true);
        }} />
    </Box>
  );
};
